export const GOLD = '#FFAC03';

export const numberOrNull = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const money = (value) => {
  const n = numberOrNull(value);
  if (n === null) return 'n/a';
  const abs = Math.abs(n);
  if (abs >= 1e9) return `${n < 0 ? '-' : ''}$${(abs / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${n < 0 ? '-' : ''}$${(abs / 1e6).toFixed(1)}MM`;
  if (abs >= 1000) return `${n < 0 ? '-' : ''}$${(abs / 1000).toFixed(1)}K`;
  return `${n < 0 ? '-' : ''}$${abs.toFixed(0)}`;
};

export const formatMillions = (value) => {
  const n = numberOrNull(value);
  return n === null ? 'n/a' : `${n < 0 ? '-' : ''}$${(Math.abs(n) / 1e6).toFixed(1)}MM`;
};

export const fmtNumber = (value, digits = 1) => {
  const n = numberOrNull(value);
  return n === null ? 'n/a' : n.toLocaleString(undefined, { maximumFractionDigits: digits });
};

export const scoreFactors = (finding = {}) => {
  const scoring = finding?.citations?.scoring || {};
  return {
    z: Number(scoring.z ?? scoring.surpriseZ ?? finding.surpriseZ ?? 0),
    // scoring payload carries the formula but not the factor itself — derive it
    // exactly as documented: log10(1+|dollar_impact|)
    materiality: Number(
      scoring.logMateriality ?? scoring.log_materiality ?? scoring.materiality
        ?? Math.log10(1 + Math.abs(Number(finding.dollarImpact ?? 0)))
    ),
    weight: Number(scoring.weight ?? scoring.relevanceWeight ?? finding.relevanceWeight ?? 0)
  };
};

export const classifyBandBreach = (value, baseline = {}, k = 1.4826) => {
  const n = numberOrNull(value);
  const median = numberOrNull(baseline?.median);
  const mad = Math.max(0, numberOrNull(baseline?.mad) ?? 0);
  if (n === null || median === null) {
    return { breached: false, direction: 'unknown', lower: null, upper: null };
  }
  const spread = k * mad;
  const lower = median - spread;
  const upper = median + spread;
  if (n > upper) return { breached: true, direction: 'high', lower, upper };
  if (n < lower) return { breached: true, direction: 'low', lower, upper };
  return { breached: false, direction: 'inside', lower, upper };
};

const periodDaysInMonth = (period) => {
  const match = String(period || '').match(/^(\d{4})-(\d{2})/);
  if (!match) return 31;
  return new Date(Number(match[1]), Number(match[2]), 0).getDate();
};

export const buildDayDomain = (months = [], fallbackPeriod) => {
  const rows = Array.isArray(months) ? months : [];
  const maxFromRows = rows.reduce((max, month) => {
    const dayMax = Number(month?.maxDay);
    const daysMax = Array.isArray(month?.days)
      ? month.days.reduce((innerMax, day) => Math.max(innerMax, Number(day?.day) || 0), 0)
      : 0;
    return Math.max(max, Number.isFinite(dayMax) ? dayMax : 0, daysMax);
  }, 0);
  const period = rows.find((month) => month?.isAsOf)?.period || rows[0]?.period || fallbackPeriod;
  const closeDay = periodDaysInMonth(period);
  const maxDay = Math.max(closeDay, maxFromRows || closeDay);
  return {
    maxDay,
    closeDay,
    days: Array.from({ length: maxDay }, (_, index) => index + 1),
    showCloseMarker: maxDay > closeDay
  };
};

export const formatAmountVsTypical = (value) => {
  const n = numberOrNull(value);
  if (n === null || Math.abs(n) < 5) return 'in line';
  return `${n > 0 ? '+' : ''}${Math.round(n)}%`;
};

const isOpenFinding = (finding) => {
  const status = String(finding?.status || '').toLowerCase();
  const disposition = finding?.disposition || finding?.verdict;
  return !disposition && !['closed', 'dispositioned', 'resolved'].includes(status);
};

export const selectNextOpenFinding = (findings = [], currentId) => {
  const rows = Array.isArray(findings) ? findings : [];
  if (!rows.length) return '';
  const currentIndex = Math.max(0, rows.findIndex((finding) => finding.id === currentId));
  const ordered = [...rows.slice(currentIndex + 1), ...rows.slice(0, currentIndex)];
  return ordered.find(isOpenFinding)?.id || rows[currentIndex]?.id || rows[0]?.id || '';
};

export const transactionDayIndex = (txn = {}) => {
  const n = numberOrNull(txn.dayIndex ?? txn.day_index);
  if (n !== null) return n;
  const day = String(txn.postingDate || txn.posting_date || '').slice(-2);
  return Number(day) || null;
};

export const sortTransactionsByMagnitude = (transactions = []) =>
  [...(Array.isArray(transactions) ? transactions : [])].sort(
    (a, b) => Math.abs(Number(b?.amount) || 0) - Math.abs(Number(a?.amount) || 0)
  );
